export type RewardsRow = {
  id: string;
  kind: 'CONTRACT' | 'COMPENSATION' | 'BENEFIT';
  descriptor: string;
  detail: string;
  period: string;
  status: string;
};

export type RewardWorkerOption = {
  id: string;
  label: string;
};

type RewardReference = {
  descriptor?: string;
};

export type WorkerRewardsSnapshot = {
  worker?: { id: string; descriptor?: string };
  contracts: ReadonlyArray<{
    id: string;
    contractType: string;
    contractStatus: string;
    weeklyHours?: string;
    effectiveFrom: string;
  }>;
  compensation: ReadonlyArray<{
    id: string;
    compensationType: string;
    currencyCode: string;
    payFrequency?: string;
    amount?: string;
    protected?: boolean;
    effectiveFrom: string;
  }>;
  benefitEnrollments: ReadonlyArray<{
    id: string;
    benefitPlan: RewardReference;
    coverageLevel: string;
    enrollmentStatus: string;
    effectiveFrom: string;
  }>;
};

export type RewardChangeInput = {
  changeType: 'COMPENSATION';
  worker: { id: string };
  effectiveDate: string;
  compensation: {
    compensationType: 'BASE_SALARY';
    currencyCode: string;
    payFrequency: string;
    amount: string;
  };
};

export interface RewardsApi {
  listWorkers(search: string, asOf: string): Promise<RewardWorkerOption[]>;
  listAllWorkers(): Promise<RewardWorkerOption[]>;
  getWorkerRewards(workerId: string): Promise<WorkerRewardsSnapshot>;
  createRewardChange(input: RewardChangeInput, idempotencyKey: string): Promise<{ id: string }>;
  submitRewardChange(changeId: string, idempotencyKey: string): Promise<unknown>;
  isDefinitiveRequestFailure(reason: unknown): boolean;
}

export function rewardsRows(snapshot: WorkerRewardsSnapshot): RewardsRow[] {
  return [
    ...snapshot.contracts.map((item): RewardsRow => ({
      id: item.id,
      kind: 'CONTRACT',
      descriptor: item.contractType,
      detail: `${item.weeklyHours ?? '-'}시간`,
      period: item.effectiveFrom,
      status: item.contractStatus,
    })),
    ...snapshot.compensation.map((item): RewardsRow => ({
      id: item.id,
      kind: 'COMPENSATION',
      descriptor: item.compensationType,
      detail: item.protected ? '권한 필요' : `${item.currencyCode} ${item.amount ?? '-'}`,
      period: item.effectiveFrom,
      status: 'ACTIVE',
    })),
    ...snapshot.benefitEnrollments.map((item): RewardsRow => ({
      id: item.id,
      kind: 'BENEFIT',
      descriptor: item.benefitPlan.descriptor ?? '복리후생',
      detail: item.coverageLevel,
      period: item.effectiveFrom,
      status: item.enrollmentStatus,
    })),
  ];
}

export function rewardStatusLabel(value: string) {
  return ({
    ACTIVE: '사용 중',
    VALIDATED: '검증 완료',
    COMPLETED: '완료',
    COMPLETED_WITH_ERRORS: '일부 실패',
    PROCESSING: '처리 중',
    QUEUED: '대기',
    DRAFT: '작성 중',
    OPEN: '채용 가능',
    FROZEN: '보류',
    FILLED: '충원',
    CLOSED: '종료',
    DISABLED: '사용 안 함',
    REVOKED: '해지됨',
  } as Record<string, string>)[value] ?? value;
}

export function rewardStatusTone(value: string): 'success' | 'warning' | 'neutral' {
  if (['ACTIVE', 'VALIDATED', 'COMPLETED', 'ENABLED', 'OPEN', 'FILLED'].includes(value)) return 'success';
  if (['PROCESSING', 'QUEUED', 'FROZEN', 'DRAFT'].includes(value)) return 'warning';
  return 'neutral';
}

export function rewardsReadError(error: Error): string {
  if ('status' in error && (error.status === 404 || error.status === 403)) return '선택한 구성원의 보상 정보를 조회할 수 없습니다. 조회 가능한 구성원을 다시 선택하거나 관리자에게 권한을 확인해 주세요.';
  return '보상 정보를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.';
}
export function salaryDescription(amount: string, currency: string, frequency?: string): string {
  const value = amount.trim();
  const formatted = value && Number.isFinite(Number(value)) ? new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 4 }).format(Number(value)) : value || '미입력';
  return [currency, formatted, frequency === 'ANNUAL' ? '연간' : frequency === 'MONTHLY' ? '월간' : frequency].filter(Boolean).join(' · ');
}
export function currentSalaryDescription(snapshot: WorkerRewardsSnapshot): string {
  const salary = snapshot.compensation.filter((item) => item.compensationType === 'BASE_SALARY').sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom))[0];
  if (!salary) return '등록된 기본급 없음';
  if (salary.protected || salary.amount === undefined) return '보호된 금액 · 조회 권한 필요';
  return salaryDescription(salary.amount, salary.currencyCode, salary.payFrequency);
}
